/**
 * Centro Ejecutivo — KPIs estratégicos y ranking DMA.
 *
 * Reemplaza la antigua "RoleHome".  Es la página por defecto al
 * autenticarse: muestra el estado de la red (NRW global, recuperable,
 * SLA, cobertura AMI) y permite drill-down hacia el Centro de Alertas
 * o el Mapa con un click.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { api } from "../api/client";
import { Icon } from "../components/Icon";
import { InsightCard } from "../components/InsightCard";
import { Sparkline } from "../components/Sparkline";
import { Section } from "../components/Section";
import { PageHeader } from "../layout/PageHeader";
import { loadInsightsBundle, kpiIntelligence } from "../lib/insightsRuntime";

interface ExecutiveKpis {
  nrw_red_pct: number;
  nrw_red_delta_pp: number;
  volumen_recuperable_m3: number;
  perdida_evitada_clp: number;
  roi_factor: number;
  eficiencia_cuadrillas_pct: number;
  cumplimiento_sla_pct: number;
  cobertura_ami_pct: number;
  tiempo_medio_cierre_h: number;
  alertas_abiertas: number;
  alertas_criticas: number;
  ranking: Array<{ dma_codigo: string; nrw_pct: number; ili: number; mnf_lps: number }>;
  trend_nrw_red: number[];
  trend_recuperable: number[];
}

function formatNumber(n: number, frac = 0): string {
  return n.toLocaleString("es-CL", {
    minimumFractionDigits: frac,
    maximumFractionDigits: frac,
  });
}

function formatCLP(n: number): string {
  if (n > 1_000_000_000) return `CLP ${(n / 1_000_000_000).toFixed(1)} B`;
  if (n > 1_000_000) return `CLP ${(n / 1_000_000).toFixed(1)} M`;
  if (n > 1_000) return `CLP ${(n / 1_000).toFixed(0)} k`;
  return `CLP ${n.toFixed(0)}`;
}

export function ExecutiveCenter(): JSX.Element {
  const kpisQuery = useQuery<ExecutiveKpis>({
    queryKey: ["executive.kpis"],
    queryFn: () => api<ExecutiveKpis>("/executive/kpis"),
    staleTime: 60_000,
  });

  const k = kpisQuery.data;

  // Motor_Insights: Insights ejecutivos correlacionados sobre el
  // Grafo_Entidades, consumidos vía el runtime selector (R5.7).
  const motorQuery = useQuery({
    queryKey: ["motor-insights"],
    queryFn: loadInsightsBundle,
    staleTime: 60_000,
  });
  const motorInsights = motorQuery.data?.insights ?? [];
  // Top insights por confianza para la vista ejecutiva.
  const topInsights = [...motorInsights].sort((a, b) => b.confianza - a.confianza).slice(0, 3);

  // KPI intelligence del NRW red frente al periodo anterior (R5.5).
  const nrwKpi =
    k != null
      ? kpiIntelligence("NRW red", k.nrw_red_pct - k.nrw_red_delta_pp, k.nrw_red_pct)
      : null;

  const nrwSeries =
    k?.trend_nrw_red.map((v, i) => ({ idx: `M-${k.trend_nrw_red.length - i}`, nrw: v })) ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Vista global"
        title="Centro Ejecutivo"
        subtitle="Monitorea NRW, alertas críticas, cumplimiento SLA y ROI de la operación."
        actions={
          <>
            <Link to="/alertas" className="btn">
              <Icon name="bell" size={13} /> Alertas
            </Link>
            <Link to="/mapa" className="btn btn--primary">
              <Icon name="search" size={13} /> Mapa Operacional
            </Link>
          </>
        }
      />

      {k && (
        <>
          {/* Franja KPI compacta — siempre visible, alta densidad sin ruido */}
          <div className="kpi-strip">
            <div className="kpi-mini">
              <span className="kpi-mini__label">NRW red</span>
              <span className="kpi-mini__value">{formatNumber(k.nrw_red_pct, 1)} %</span>
              <span className="kpi-mini__hint">
                <span
                  className={`kpi__delta kpi__delta--${k.nrw_red_delta_pp < 0 ? "up" : "down"}`}
                >
                  {k.nrw_red_delta_pp < 0 ? "▼" : "▲"} {Math.abs(k.nrw_red_delta_pp).toFixed(1)} pp
                </span>
              </span>
            </div>
            <div className="kpi-mini kpi-mini--accent">
              <span className="kpi-mini__label">Recuperable</span>
              <span className="kpi-mini__value">{formatNumber(k.volumen_recuperable_m3)} m³</span>
              <span className="kpi-mini__hint">{k.alertas_abiertas} alertas abiertas</span>
            </div>
            <div className="kpi-mini kpi-mini--info">
              <span className="kpi-mini__label">Pérdida evitada</span>
              <span className="kpi-mini__value">{formatCLP(k.perdida_evitada_clp)}</span>
              <span className="kpi-mini__hint">ROI ×{k.roi_factor.toFixed(1)}</span>
            </div>
            <div className="kpi-mini kpi-mini--warn">
              <span className="kpi-mini__label">SLA</span>
              <span className="kpi-mini__value">{k.cumplimiento_sla_pct} %</span>
              <span className="kpi-mini__hint">MTTR {k.tiempo_medio_cierre_h} h</span>
            </div>
            <div className="kpi-mini kpi-mini--magenta">
              <span className="kpi-mini__label">Cobertura AMI</span>
              <span className="kpi-mini__value">{k.cobertura_ami_pct.toFixed(1)} %</span>
              <span className="kpi-mini__hint">lecturas válidas</span>
            </div>
            <div className="kpi-mini kpi-mini--danger">
              <span className="kpi-mini__label">Alertas críticas</span>
              <span className="kpi-mini__value">{k.alertas_criticas}</span>
              <span className="kpi-mini__hint">acción {"<"} 8 h</span>
            </div>
          </div>

          {/* Tendencias — colapsable */}
          <Section
            id="exec.tendencias"
            title="Tendencias"
            icon="dashboard"
            summary={
              <>
                <span>NRW {formatNumber(k.nrw_red_pct, 1)}%</span>
                <span>Eficiencia {k.eficiencia_cuadrillas_pct}%</span>
              </>
            }
          >
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
              <div>
                <div className="card__title">
                  <span>Evolución NRW red (últimos 6 periodos)</span>
                  <span className="text-muted text-mono">target ≤ 22 %</span>
                </div>
                <div style={{ width: "100%", height: 200 }}>
                  <ResponsiveContainer>
                    <AreaChart data={nrwSeries} margin={{ top: 6, right: 8, bottom: 0, left: -6 }}>
                      <defs>
                        <linearGradient id="grad-nrw" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--c-primary-300)" stopOpacity={0.55} />
                          <stop offset="95%" stopColor="var(--c-primary-300)" stopOpacity={0.04} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                      <XAxis dataKey="idx" stroke="var(--chart-axis)" tick={{ fontSize: 11 }} />
                      <YAxis stroke="var(--chart-axis)" tick={{ fontSize: 11 }} domain={[20, 32]} />
                      <Tooltip
                        contentStyle={{
                          background: "var(--c-surface)",
                          border: "1px solid var(--c-border)",
                          borderRadius: 8,
                          fontSize: 12,
                        }}
                        formatter={(v: number) => [`${v.toFixed(2)} %`, "NRW"]}
                      />
                      <Area
                        type="monotone"
                        dataKey="nrw"
                        stroke="var(--c-primary-300)"
                        strokeWidth={2}
                        fill="url(#grad-nrw)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div>
                <div className="card__title">
                  <span>Eficiencia cuadrillas</span>
                </div>
                <div className="kpi__value" style={{ fontSize: 32 }}>
                  {k.eficiencia_cuadrillas_pct} %
                </div>
                <Sparkline values={[58, 62, 66, 71, 74, 76, k.eficiencia_cuadrillas_pct]} />
                <div className="text-muted" style={{ fontSize: 12 }}>
                  % órdenes cerradas con confirmación de hallazgo
                </div>
              </div>
            </div>
          </Section>

          {/* Ranking DMA — colapsable */}
          <Section
            id="exec.ranking"
            title="Ranking DMAs por NRW"
            icon="droplet"
            summary={<span>{k.ranking.length} DMAs · top {k.ranking[0]?.dma_codigo}</span>}
          >
            <div className="table-wrapper" style={{ borderColor: "transparent" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>DMA</th>
                    <th className="numeric">NRW %</th>
                    <th className="numeric">ILI</th>
                    <th className="numeric">MNF (L/s)</th>
                    <th>Tendencia</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {k.ranking.map((row) => {
                    const sev =
                      row.nrw_pct >= 30 ? "danger" : row.nrw_pct >= 25 ? "warn" : "accent";
                    const trendValues = Array.from({ length: 8 }, (_, i) =>
                      row.nrw_pct - 0.4 * (8 - i) + (i % 3) * 0.3,
                    );
                    return (
                      <tr key={row.dma_codigo}>
                        <td>
                          <strong>{row.dma_codigo}</strong>
                        </td>
                        <td className="numeric">
                          <span className={`badge badge--${sev}`}>
                            {row.nrw_pct.toFixed(1)} %
                          </span>
                        </td>
                        <td className="numeric">{row.ili.toFixed(2)}</td>
                        <td className="numeric">{row.mnf_lps.toFixed(2)}</td>
                        <td style={{ width: 160 }}>
                          <Sparkline
                            values={trendValues}
                            stroke={
                              sev === "danger"
                                ? "var(--c-danger)"
                                : sev === "warn"
                                  ? "var(--c-warn)"
                                  : "var(--c-success)"
                            }
                            fill="rgba(255,255,255,0.04)"
                            height={28}
                          />
                        </td>
                        <td>
                          <Link className="btn btn--ghost" to={`/mapa?dma=${row.dma_codigo}`}>
                            Abrir <Icon name="chevron-right" size={12} />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>

          {/* Operational Insights — Motor_Insights (R5.5, R5.7) */}
          <Section
            id="exec.insights"
            title="Operational Insights"
            icon="flask"
            summary={<span>{motorInsights.length} insights · top {topInsights.length}</span>}
          >
            {nrwKpi && (
              <div
                className="status-block"
                style={{
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 2,
                  marginBottom: 12,
                  background: "var(--c-surface-2)",
                  borderColor: "var(--c-border)",
                }}
              >
                <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  KPI intelligence · {nrwKpi.kpi}
                </strong>
                <span style={{ color: "var(--c-text)", fontSize: 12.5 }}>
                  {nrwKpi.direccion === "incremento" ? "▲" : "▼"} {nrwKpi.magnitud_pct.toFixed(1)}% ·{" "}
                  {nrwKpi.causa_probable}
                </span>
              </div>
            )}
            {motorQuery.isLoading && (
              <div className="status-block status-block--info">Correlacionando señales…</div>
            )}
            {!motorQuery.isLoading && topInsights.length === 0 && (
              <div className="status-block">Sin Insights fundamentados disponibles.</div>
            )}
            <div className="vstack">
              {topInsights.map((ins) => (
                <InsightCard key={ins.insight_id} insight={ins} compact />
              ))}
            </div>
            {motorInsights.length > 0 && (
              <Link to="/insights" className="btn btn--ghost" style={{ marginTop: 8 }}>
                Ver todos los Insights <Icon name="chevron-right" size={12} />
              </Link>
            )}
          </Section>
        </>
      )}
    </>
  );
}
